import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Upload,
  Download,
  Trash2,
  RefreshCw,
  File,
  Folder,
  FolderPlus,
  AlertCircle,
  X,
  ChevronRight,
  Home,
  Eye,
  Copy,
  FileText,
  Image,
  FileJson,
  Lock,
  ShieldAlert,
  Video,
  Music,
  FileType,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCDNFiles, useCDNStats, useUploadFile, useDeleteFile, useCDNVersion } from '../hooks/useCDN';
import ApprovalRequestModal from '../components/ApprovalRequestModal';
import { useToast } from '../components/ui';
import { CDNFile } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { validateFile } from '../utils/validation';

interface FileWithError {
  file: File;
  error?: string;
}

interface FolderItem {
  name: string;
  path: string;
  isFolder: true;
  itemCount: number;
  description?: string;
  isLocked?: boolean;
}

interface FileItem extends CDNFile {
  name: string;
  isFolder: false;
}

type BrowserItem = FolderItem | FileItem;

interface CDNManifest {
  version: string;
  lastUpdated: string;
  structure: Record<string, FolderStructure>;
  lockedPaths: string[];
}

interface FolderStructure {
  description: string;
  locked?: boolean;
  allowedTypes?: string[];
  maxSizeMB?: number;
  subfolders?: Record<string, FolderStructure>;
}

// Get the CDN URL from environment
const CDN_URL = import.meta.env.VITE_CONTENT_CDN_URL || import.meta.env.VITE_CDN_URL || '';

export default function CDNManagement() {
  const [currentPath, setCurrentPath] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<FileWithError[]>([]);
  const [deletingFile, setDeletingFile] = useState<CDNFile | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [viewingFile, setViewingFile] = useState<CDNFile | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: BrowserItem } | null>(null);
  const [manifest, setManifest] = useState<CDNManifest | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: allFiles, isLoading, refetch } = useCDNFiles('');
  const { data: stats } = useCDNStats();
  const { data: versionInfo } = useCDNVersion();
  const uploadMutation = useUploadFile();
  const deleteMutation = useDeleteFile();
  const toast = useToast();

  // Load the manifest from CDN
  useEffect(() => {
    const loadManifest = async () => {
      try {
        // Try to load from the CDN bucket directly via the API
        const manifestUrl = `${CDN_URL}/config/manifest.json`;
        const response = await fetch(manifestUrl);
        if (response.ok) {
          const data = await response.json();
          setManifest(data);
        }
      } catch {
        // Manifest not found, use defaults
        console.log('No manifest found, using default locked paths');
      }
    };
    loadManifest();
  }, []);

  // Get locked paths from manifest or use defaults
  const lockedPaths = useMemo(() => {
    if (manifest?.lockedPaths) {
      return manifest.lockedPaths;
    }
    // Default locked paths
    return [
      'admin/',
      'assets/',
      'i18n/',
      'index.html',
      'config/',
    ];
  }, [manifest]);

  /**
   * Check if a file or folder path is protected
   */
  const isProtectedPath = (path: string): boolean => {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return lockedPaths.some((lockedPath) => {
      if (lockedPath.endsWith('/')) {
        // Folder pattern - check if path starts with it
        return normalizedPath.startsWith(lockedPath) || normalizedPath === lockedPath.slice(0, -1);
      }
      // Exact file match
      return normalizedPath === lockedPath;
    });
  };

  /**
   * Check if the current path is within a protected area
   */
  const isInProtectedPath = (path: string): boolean => {
    return isProtectedPath(path);
  };

  /**
   * Get folder info from manifest
   */
  const getFolderInfo = (folderPath: string): { description?: string; locked?: boolean; allowedTypes?: string[] } => {
    if (!manifest?.structure) return {};

    const parts = folderPath.split('/').filter(Boolean);
    if (parts.length === 0) return {};

    let current: FolderStructure | undefined = manifest.structure[parts[0]];
    if (!current) return {};

    for (let i = 1; i < parts.length; i++) {
      if (current.subfolders && current.subfolders[parts[i]]) {
        current = current.subfolders[parts[i]];
      } else {
        return {};
      }
    }

    return {
      description: current.description,
      locked: current.locked,
      allowedTypes: current.allowedTypes,
    };
  };

  // Parse files into folder structure
  const browserItems = useMemo((): BrowserItem[] => {
    if (!allFiles) return [];

    const folders = new Map<string, number>();
    const filesInCurrentPath: FileItem[] = [];

    allFiles.forEach((file) => {
      const relativePath = currentPath ? file.key.slice(currentPath.length) : file.key;

      // Skip files not in current path
      if (currentPath && !file.key.startsWith(currentPath)) return;

      const parts = relativePath.split('/').filter(Boolean);

      if (parts.length === 0) return;

      if (parts.length === 1) {
        // File in current directory
        filesInCurrentPath.push({
          ...file,
          name: parts[0],
          isFolder: false,
        });
      } else {
        // Folder
        const folderName = parts[0];
        const folderPath = currentPath + folderName + '/';
        folders.set(folderPath, (folders.get(folderPath) || 0) + 1);
      }
    });

    const folderItems: FolderItem[] = Array.from(folders.entries()).map(([path, count]) => {
      const folderInfo = getFolderInfo(path);
      return {
        name: path.split('/').filter(Boolean).pop() || '',
        path,
        isFolder: true,
        itemCount: count,
        description: folderInfo.description,
        isLocked: folderInfo.locked || isProtectedPath(path),
      };
    });

    // Sort: folders first, then files, both alphabetically
    return [
      ...folderItems.sort((a, b) => a.name.localeCompare(b.name)),
      ...filesInCurrentPath.sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [allFiles, currentPath]);

  // Breadcrumb parts
  const pathParts = useMemo(() => {
    if (!currentPath) return [];
    return currentPath.split('/').filter(Boolean);
  }, [currentPath]);

  const navigateToPath = (path: string) => {
    setCurrentPath(path);
    setSelectedItems(new Set());
  };

  const handleItemClick = (item: BrowserItem) => {
    if (item.isFolder) {
      navigateToPath(item.path);
    }
  };

  const handleItemDoubleClick = (item: BrowserItem) => {
    if (!item.isFolder) {
      handleViewFile(item);
    }
  };

  const handleViewFile = async (file: FileItem) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const isTextFile = ['json', 'txt', 'md', 'yaml', 'yml', 'xml', 'html', 'css', 'js', 'ts'].includes(ext || '');

    if (isTextFile) {
      setViewingFile(file);
      setIsLoadingContent(true);
      try {
        const response = await fetch(file.url);
        const text = await response.text();
        setFileContent(text);
      } catch {
        setFileContent('Failed to load file content');
      }
      setIsLoadingContent(false);
    } else {
      // For non-text files, open in new tab
      window.open(file.url, '_blank');
    }
  };

  const handleSelectItem = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedItems);
    if (e.shiftKey) {
      // Range select could be implemented here
    } else if (e.ctrlKey || e.metaKey) {
      if (newSelected.has(key)) {
        newSelected.delete(key);
      } else {
        newSelected.add(key);
      }
    } else {
      newSelected.clear();
      newSelected.add(key);
    }
    setSelectedItems(newSelected);
  };

  const handleContextMenu = (e: React.MouseEvent, item: BrowserItem) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const filesWithValidation = selected.map((file) => {
      const result = validateFile(file, { maxSize: 50 * 1024 * 1024 });
      return { file, error: result.error };
    });
    setUploadingFiles(filesWithValidation);
  };

  const handleUpload = async () => {
    const validFiles = uploadingFiles.filter((f) => !f.error);
    for (const { file } of validFiles) {
      await uploadMutation.mutateAsync({
        file,
        path: `${currentPath}${file.name}`,
      });
    }
    setUploadingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    // S3 doesn't have real folders, but we can create a placeholder
    const placeholderPath = `${currentPath}${newFolderName.trim()}/.folder`;
    // Create a minimal placeholder file for the folder
    const blob = new Blob([''], { type: 'text/plain' });
    // Construct the file using Object.assign to avoid TS strict mode issues
    const placeholderFile = Object.assign(blob, {
      name: '.folder',
      lastModified: Date.now(),
      webkitRelativePath: '',
    }) as File;

    uploadMutation.mutate({
      file: placeholderFile,
      path: placeholderPath,
      contentType: 'text/plain',
    });

    setShowCreateFolderModal(false);
    setNewFolderName('');
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext || '')) {
      return <Image className="w-5 h-5 text-green-400" />;
    }
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext || '')) {
      return <Video className="w-5 h-5 text-purple-400" />;
    }
    if (['mp3', 'wav', 'ogg', 'aac'].includes(ext || '')) {
      return <Music className="w-5 h-5 text-pink-400" />;
    }
    if (['pdf', 'doc', 'docx'].includes(ext || '')) {
      return <FileType className="w-5 h-5 text-red-400" />;
    }
    if (['json', 'yaml', 'yml'].includes(ext || '')) {
      return <FileJson className="w-5 h-5 text-yellow-400" />;
    }
    if (['txt', 'md', 'html', 'css', 'js', 'ts'].includes(ext || '')) {
      return <FileText className="w-5 h-5 text-blue-400" />;
    }
    if (['woff', 'woff2', 'ttf', 'otf'].includes(ext || '')) {
      return <FileType className="w-5 h-5 text-orange-400" />;
    }
    return <File className="w-5 h-5 text-gray-400" />;
  };

  const getFolderIcon = (folderName: string) => {
    // Return different colored folder icons based on folder type
    switch (folderName) {
      case 'images':
        return <Folder className="w-5 h-5 text-green-400 flex-shrink-0" />;
      case 'videos':
        return <Folder className="w-5 h-5 text-purple-400 flex-shrink-0" />;
      case 'audio':
        return <Folder className="w-5 h-5 text-pink-400 flex-shrink-0" />;
      case 'documents':
        return <Folder className="w-5 h-5 text-red-400 flex-shrink-0" />;
      case 'fonts':
        return <Folder className="w-5 h-5 text-orange-400 flex-shrink-0" />;
      case 'config':
        return <Folder className="w-5 h-5 text-gray-400 flex-shrink-0" />;
      case 'admin':
      case 'assets':
        return <Folder className="w-5 h-5 text-blue-400 flex-shrink-0" />;
      default:
        return <Folder className="w-5 h-5 text-yellow-400 flex-shrink-0" />;
    }
  };

  const validFilesCount = uploadingFiles.filter((f) => !f.error).length;
  const hasErrors = uploadingFiles.some((f) => f.error);
  const inProtectedArea = isInProtectedPath(currentPath);

  return (
    <div className="space-y-6 animate-fade-in" onClick={() => setContextMenu(null)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">CDN File Browser</h1>
          <p className="text-gray-text">Browse and manage files on your CloudFront CDN</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => refetch()} className="btn-secondary flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowCreateFolderModal(true)}
            disabled={inProtectedArea}
            className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={inProtectedArea ? 'Cannot create folders in protected area' : 'Create new folder'}
          >
            <FolderPlus className="w-4 h-4" />
            <span>New Folder</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={inProtectedArea}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={inProtectedArea ? 'Cannot upload files to protected area' : 'Upload files'}
          >
            <Upload className="w-4 h-4" />
            <span>Upload</span>
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" disabled={inProtectedArea} />
        </div>
      </div>

      {/* Protected Area Warning */}
      {inProtectedArea && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start space-x-3">
          <ShieldAlert className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">Protected Area</p>
            <p className="text-yellow-400/80 text-sm mt-1">
              This folder contains website assets that are managed by the deployment process.
              Files here cannot be modified, deleted, or uploaded through this interface.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-card p-4">
            <p className="text-gray-text text-sm mb-1">Total Files</p>
            <p className="text-xl font-bold text-white">{stats.totalFiles}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-gray-text text-sm mb-1">Total Size</p>
            <p className="text-xl font-bold text-white">{formatBytes(stats.totalSize)}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-gray-text text-sm mb-1">Version</p>
            <p className="text-xl font-bold text-white font-mono">{stats.currentVersion?.substring(0, 8) || 'N/A'}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-gray-text text-sm mb-1">Last Published</p>
            <p className="text-white text-sm">
              {versionInfo ? formatDistanceToNow(new Date(versionInfo.timestamp), { addSuffix: true }) : 'Unknown'}
            </p>
          </div>
        </div>
      )}

      {/* Upload Queue */}
      {uploadingFiles.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                Ready to Upload ({validFilesCount} of {uploadingFiles.length})
              </h3>
              {hasErrors && <p className="text-sm text-yellow-400 mt-1">Some files have validation errors</p>}
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => setUploadingFiles([])} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={validFilesCount === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload {validFilesCount > 0 ? `${validFilesCount} File${validFilesCount > 1 ? 's' : ''}` : ''}
              </button>
            </div>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {uploadingFiles.map(({ file, error }, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  error ? 'bg-red-500/10 border border-red-500/20' : 'bg-dark-lighter'
                }`}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileIcon(file.name)}
                  <div className="min-w-0">
                    <p className={`truncate ${error ? 'text-red-400' : 'text-white'}`}>{file.name}</p>
                    <p className="text-xs text-gray-text">{formatBytes(file.size)}</p>
                    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
                  </div>
                </div>
                <button
                  onClick={() => setUploadingFiles(uploadingFiles.filter((_, i) => i !== idx))}
                  className="text-gray-text hover:text-white flex-shrink-0 ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="glass-card p-4">
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => navigateToPath('')}
            className="flex items-center space-x-1 text-primary hover:text-primary-light transition-colors"
          >
            <Home className="w-4 h-4" />
            <span>Root</span>
          </button>
          {pathParts.map((part, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <ChevronRight className="w-4 h-4 text-gray-text" />
              <button
                onClick={() => navigateToPath(pathParts.slice(0, idx + 1).join('/') + '/')}
                className="text-primary hover:text-primary-light transition-colors"
              >
                {part}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* File Browser */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-text">Loading files...</p>
          </div>
        ) : browserItems.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-text mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              {currentPath ? 'This folder is empty' : 'No files found'}
            </h3>
            <p className="text-gray-text mb-6">
              {currentPath ? 'Upload files or create subfolders' : 'Upload files to get started'}
            </p>
            <div className="flex items-center justify-center space-x-3">
              <button onClick={() => setShowCreateFolderModal(true)} className="btn-secondary">
                Create Folder
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="btn-primary">
                Upload Files
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-dark-lighter px-6 py-3 border-b border-gray-border">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-text">
                <div className="col-span-6">Name</div>
                <div className="col-span-2">Size</div>
                <div className="col-span-2">Modified</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
            </div>

            {/* Items */}
            <div className="divide-y divide-gray-border custom-scrollbar max-h-[500px] overflow-y-auto">
              {browserItems.map((item) => (
                <div
                  key={item.isFolder ? item.path : item.key}
                  className={`px-6 py-3 hover:bg-dark-lighter transition-colors cursor-pointer ${
                    selectedItems.has(item.isFolder ? item.path : item.key) ? 'bg-primary/10' : ''
                  }`}
                  onClick={(e) => handleSelectItem(item.isFolder ? item.path : item.key, e)}
                  onDoubleClick={() => (item.isFolder ? handleItemClick(item) : handleItemDoubleClick(item))}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-6 flex items-center space-x-3">
                      {item.isFolder ? (
                        getFolderIcon(item.name)
                      ) : (
                        getFileIcon(item.name)
                      )}
                      <div className="min-w-0">
                        <p className="text-white truncate">{item.name}</p>
                        {item.isFolder && (
                          <div className="flex items-center space-x-2">
                            <p className="text-xs text-gray-text">{item.itemCount} item{item.itemCount !== 1 ? 's' : ''}</p>
                            {item.description && (
                              <span className="text-xs text-gray-text/70">• {item.description}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-white text-sm">{item.isFolder ? '-' : formatBytes(item.size)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-white text-sm">
                        {item.isFolder ? '-' : formatDistanceToNow(new Date(item.lastModified), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="col-span-2 flex items-center justify-end space-x-1">
                      {item.isFolder ? (
                        <>
                          {item.isLocked && (
                            <span title="Protected folder - cannot modify contents">
                              <Lock className="w-4 h-4 text-yellow-400" />
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleItemClick(item);
                            }}
                            className="p-2 text-gray-text hover:text-primary transition-colors"
                            title="Open Folder"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          {isProtectedPath(item.key) && (
                            <span title="Protected file">
                              <Lock className="w-4 h-4 text-yellow-400" />
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewFile(item);
                            }}
                            className="p-2 text-gray-text hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyUrl(item.url);
                            }}
                            className="p-2 text-gray-text hover:text-primary transition-colors"
                            title="Copy URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-text hover:text-primary transition-colors"
                            title="Download"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          {!isProtectedPath(item.key) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingFile(item);
                              }}
                              className="p-2 text-gray-text hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-dark-card border border-gray-border rounded-lg shadow-xl py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {contextMenu.item.isFolder ? (
            <>
              <button
                onClick={() => {
                  handleItemClick(contextMenu.item);
                  setContextMenu(null);
                }}
                className="w-full px-4 py-2 text-left text-white hover:bg-dark-lighter flex items-center space-x-2"
              >
                <Folder className="w-4 h-4" />
                <span>Open</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  handleViewFile(contextMenu.item as FileItem);
                  setContextMenu(null);
                }}
                className="w-full px-4 py-2 text-left text-white hover:bg-dark-lighter flex items-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>View</span>
              </button>
              <button
                onClick={() => {
                  handleCopyUrl((contextMenu.item as FileItem).url);
                  setContextMenu(null);
                }}
                className="w-full px-4 py-2 text-left text-white hover:bg-dark-lighter flex items-center space-x-2"
              >
                <Copy className="w-4 h-4" />
                <span>Copy URL</span>
              </button>
              <a
                href={(contextMenu.item as FileItem).url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-4 py-2 text-left text-white hover:bg-dark-lighter flex items-center space-x-2"
                onClick={() => setContextMenu(null)}
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </a>
              {!isProtectedPath((contextMenu.item as FileItem).key) && (
                <>
                  <hr className="border-gray-border my-1" />
                  <button
                    onClick={() => {
                      setDeletingFile(contextMenu.item as FileItem);
                      setContextMenu(null);
                    }}
                    className="w-full px-4 py-2 text-left text-red-400 hover:bg-dark-lighter flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </>
              )}
              {isProtectedPath((contextMenu.item as FileItem).key) && (
                <>
                  <hr className="border-gray-border my-1" />
                  <div className="px-4 py-2 text-gray-text flex items-center space-x-2">
                    <Lock className="w-4 h-4" />
                    <span>Protected file</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-md animate-spring-in">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Create New Folder</h3>
              <p className="text-gray-text text-sm mb-4">
                Creating folder in: <span className="text-primary font-mono">/{currentPath || ''}</span>
              </p>
              <input
                type="text"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                className="input w-full mb-4"
                autoFocus
              />
              <div className="flex items-center justify-end space-x-3">
                <button onClick={() => setShowCreateFolderModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleCreateFolder} disabled={!newFolderName.trim()} className="btn-primary">
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-4xl max-h-[90vh] flex flex-col animate-spring-in">
            <div className="p-4 border-b border-gray-border flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getFileIcon(viewingFile.key.split('/').pop() || '')}
                <div>
                  <h3 className="text-lg font-semibold text-white">{viewingFile.key.split('/').pop()}</h3>
                  <p className="text-xs text-gray-text font-mono">{viewingFile.key}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleCopyUrl(viewingFile.url)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copy URL</span>
                </button>
                <a
                  href={viewingFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </a>
                <button onClick={() => setViewingFile(null)} className="p-2 text-gray-text hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {isLoadingContent ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : viewingFile.key.endsWith('.md') ? (
                <div className="prose prose-invert prose-sm max-w-none bg-dark-lighter p-6 rounded-lg overflow-x-auto
                  prose-headings:text-white prose-headings:font-semibold prose-headings:border-b prose-headings:border-gray-border prose-headings:pb-2
                  prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
                  prose-p:text-gray-300 prose-p:leading-relaxed
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white
                  prose-code:text-primary prose-code:bg-dark prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                  prose-pre:bg-dark prose-pre:border prose-pre:border-gray-border
                  prose-ul:text-gray-300 prose-ol:text-gray-300
                  prose-li:marker:text-primary
                  prose-table:border-collapse prose-table:border prose-table:border-gray-border
                  prose-th:bg-dark prose-th:border prose-th:border-gray-border prose-th:p-2 prose-th:text-white
                  prose-td:border prose-td:border-gray-border prose-td:p-2 prose-td:text-gray-300
                  prose-hr:border-gray-border">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {fileContent}
                  </ReactMarkdown>
                </div>
              ) : (
                <pre className="text-sm text-white font-mono whitespace-pre-wrap bg-dark-lighter p-4 rounded-lg overflow-x-auto">
                  {fileContent}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DM-6437: deleting a CDN file is an FM-105 destructive operation, so the
          old confirm dialog became the dual-approval modal. Protected paths never
          reach here; both trigger sites gate on isProtectedPath. */}
      {deletingFile && (
        <ApprovalRequestModal
          operation="deleteCdnFile"
          targetId={deletingFile.key}
          targetLabel={deletingFile.key}
          title={`Delete ${deletingFile.key}`}
          description="Removes the file from the CDN bucket. Anything still linking to it breaks."
          execute={async approval => {
            await deleteMutation.mutateAsync({ key: deletingFile.key, approval });
          }}
          onClose={() => setDeletingFile(null)}
          onCompleted={() => toast.success(`Deleted ${deletingFile.key}`)}
        />
      )}
    </div>
  );
}
